import React, { useCallback, useEffect, useState } from 'react';
import { FolderOpen, FileText, FileSignature, ScrollText, RefreshCw, Sparkles, Check, X, Loader2 } from 'lucide-react';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

// ── API shapes (match worker /api/documents + /api/enrichment) ────────────────

interface DocRow {
  id: number;
  document_type: string;
  label: string | null;
  original_filename: string | null;
  file_type: string | null;
  file_size: number | null;
  extraction_status: string;
  extraction_method: string | null;
  version: number;
  is_current: number;
  created_at: string;
}

interface GroupedDocs {
  resume: DocRow[];
  cv: DocRow[];
  cover_letter: DocRow[];
}

interface DocsResponse {
  documents: GroupedDocs;
  counts: { resume: number; cv: number; cover_letter: number; total: number };
}

interface Suggestion {
  id: number;
  keyword: string;
  occurrence_count: number;
  job_ids: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const EMPTY: GroupedDocs = { resume: [], cv: [], cover_letter: [] };

// ── Presentation helpers ──────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  ok: { label: 'Extracted', cls: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400' },
  ocr: { label: 'OCR (scanned)', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400' },
  low_yield: { label: 'Partial text', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400' },
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  failed: { label: 'Extraction failed', cls: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.pending;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${s.cls}`}>{s.label}</span>;
}

function fmtDate(iso: string): string {
  const d = new Date(iso.replace(' ', 'T') + (iso.includes('T') ? '' : 'Z'));
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const GROUP_META: Array<{ key: keyof GroupedDocs; title: string; icon: React.FC<{ className?: string }> }> = [
  { key: 'resume', title: 'Resumes', icon: FileText },
  { key: 'cv', title: 'CVs', icon: ScrollText },
  { key: 'cover_letter', title: 'Cover Letters', icon: FileSignature },
];

// ── Component ──────────────────────────────────────────────────────────────────

const Documents: React.FC = () => {
  const { toast } = useToast();
  const [docs, setDocs] = useState<GroupedDocs>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [evidence, setEvidence] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<DocsResponse>('/api/documents', { retry: 2 });
      setDocs(data.documents ?? EMPTY);
    } catch (e) {
      toast.error(errorText(e, 'Failed to load documents'));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadSuggestions = useCallback(async () => {
    try {
      const data = await api.get<{ suggestions: Suggestion[] }>('/api/enrichment', { retry: 2 });
      setSuggestions(data.suggestions ?? []);
    } catch (e) {
      toast.error(errorText(e, 'Failed to load suggestions'));
    }
  }, [toast]);

  useEffect(() => {
    loadDocs();
    loadSuggestions();
  }, [loadDocs, loadSuggestions]);

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await api.post<{ inserted: number; skipped: number }>('/api/documents/backfill');
      toast.success(r.inserted > 0 ? `Imported ${r.inserted} document(s)` : 'Everything is already up to date');
      await loadDocs();
    } catch (e) {
      toast.error(errorText(e, 'Backfill failed'));
    } finally {
      setBackfilling(false);
    }
  };

  const confirmSuggestion = async (id: number) => {
    if (!evidence.trim()) {
      toast.error('Describe real experience that supports this keyword.');
      return;
    }
    setBusyId(id);
    try {
      await api.post(`/api/enrichment/${id}/confirm`, { supporting_text: evidence.trim() });
      toast.success('Added to your profile facts');
      setOpenId(null);
      setEvidence('');
      await loadSuggestions();
    } catch (e) {
      toast.error(errorText(e, 'Could not confirm'));
    } finally {
      setBusyId(null);
    }
  };

  const dismissSuggestion = async (id: number) => {
    setBusyId(id);
    try {
      await api.post(`/api/enrichment/${id}/dismiss`);
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      toast.error(errorText(e, 'Could not dismiss'));
    } finally {
      setBusyId(null);
    }
  };

  const total = docs.resume.length + docs.cv.length + docs.cover_letter.length;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-primary-500/10 text-primary-600 dark:text-primary-400">
            <FolderOpen className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">My Documents</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Every resume, CV and cover letter you&apos;ve uploaded — grouped by type, with extraction status.
            </p>
          </div>
        </div>
        <button
          onClick={handleBackfill}
          disabled={backfilling}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60"
        >
          {backfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Import existing
        </button>
      </div>

      {/* Documents groups */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
        </div>
      ) : total === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500 dark:text-slate-400">
          No documents yet. Upload a resume or cover letter, or click <span className="font-medium">Import existing</span> to bring across your previous uploads.
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-3">
          {GROUP_META.map(({ key, title, icon: Icon }) => (
            <section key={key} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-navy-900 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</h2>
                <span className="ml-auto text-xs text-slate-400">{docs[key].length}</span>
              </div>
              {docs[key].length === 0 ? (
                <p className="text-xs text-slate-400 py-2">None yet</p>
              ) : (
                <ul className="space-y-2">
                  {docs[key].map((d) => (
                    <li key={d.id} className="rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={d.original_filename ?? ''}>
                          {d.label || d.original_filename || `Document #${d.id}`}
                        </span>
                        {d.version > 1 && <span className="text-[11px] text-slate-400 flex-shrink-0">v{d.version}</span>}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <StatusBadge status={d.extraction_status} />
                        <span className="text-[11px] text-slate-400">{fmtDate(d.created_at)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {/* Profile enrichment */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-navy-900 p-5">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-primary-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Profile enrichment</h2>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Keywords your target jobs ask for that your profile doesn&apos;t yet support. Confirm one with real evidence and it
          becomes a verified fact we can use — we never invent experience for you.
        </p>

        {suggestions.length === 0 ? (
          <p className="text-sm text-slate-400">No outstanding suggestions. Generate an application to surface keyword gaps.</p>
        ) : (
          <ul className="space-y-2">
            {suggestions.map((s) => (
              <li key={s.id} className="rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{s.keyword}</span>
                    <span className="text-[11px] text-slate-400">seen in {s.occurrence_count} job{s.occurrence_count === 1 ? '' : 's'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setOpenId(openId === s.id ? null : s.id); setEvidence(''); }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary-500/10 text-primary-600 dark:text-primary-400 hover:bg-primary-500/20"
                    >
                      <Check className="w-3.5 h-3.5" /> I have this
                    </button>
                    <button
                      onClick={() => dismissSuggestion(s.id)}
                      disabled={busyId === s.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60"
                    >
                      <X className="w-3.5 h-3.5" /> Dismiss
                    </button>
                  </div>
                </div>

                {openId === s.id && (
                  <div className="mt-3">
                    <textarea
                      value={evidence}
                      onChange={(e) => setEvidence(e.target.value)}
                      rows={3}
                      placeholder={`Describe real experience with "${s.keyword}" (where, when, impact). This becomes a verified profile fact.`}
                      className="w-full text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-navy-800 px-3 py-2 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <button
                        onClick={() => { setOpenId(null); setEvidence(''); }}
                        className="px-3 py-1.5 text-xs rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => confirmSuggestion(s.id)}
                        disabled={busyId === s.id || !evidence.trim()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                      >
                        {busyId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Add to profile
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Documents;
